/* eslint-disable complexity */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import { MultiSelect, Option } from 'carbon-react/lib/components/select';

import { USER_ROLE_IDS } from '../constants';
import { UserFormController } from '../types';

interface UserOrganisationSelectorProps {
  disabled?: boolean;
  form: UserFormController;
}

export const UserOrganisationSelector = ({ disabled = false, form }: UserOrganisationSelectorProps) => {
  const intl = useIntl();
  const isSuperAdmin = form.roleId === USER_ROLE_IDS.SUPER_ADMIN;
  const selectedOrganisationIds = isSuperAdmin
    ? form.organisations.map(organisation => organisation.id)
    : form.organisations
      .filter(organisation => form.selectedOrganisations[organisation.id])
      .map(organisation => organisation.id);
  const areAllOrganisationsSelected =
    form.organisations.length > 0 && selectedOrganisationIds.length === form.organisations.length;

  return (
    <>
      <MultiSelect
        disabled={disabled || isSuperAdmin}
        error={form.errors.organisations?.message}
        id="user-organisations"
        inputHint={intl.formatMessage({
          id: 'predict.workspaces.users.organisations.hint',
          defaultMessage: 'Select one or more. Type to search if needed.'
        })}
        label={intl.formatMessage({ id: 'common.add_organisations', defaultMessage: 'Add organisations' })}
        name="user-organisations"
        onBlur={form.revalidate}
        onChange={form.onChangeOrganisations}
        required={!disabled && !isSuperAdmin}
        value={selectedOrganisationIds}
        mb={0}
      >
        {form.organisations.map(organisation => (
          <Option
            data-testid={`user-organisation-option-${organisation.id}`}
            key={organisation.id}
            text={organisation.name}
            value={organisation.id}
          />
        ))}
      </MultiSelect>
      <Box display="flex" justifyContent="flex-end" mt={1} mb={1}>
        <Button
          buttonType="tertiary"
          disabled={disabled || isSuperAdmin || !form.organisations.length}
          onClick={areAllOrganisationsSelected ? form.deselectAllOrganisations : form.selectAllOrganisations}
          size="small"
          type="button"
        >
          {areAllOrganisationsSelected ? (
            <FormattedMessage id="common.deselect_all" defaultMessage="Deselect all" />
          ) : (
            <FormattedMessage id="common.select_all" defaultMessage="Select all" />
          )}
        </Button>
      </Box>
    </>
  );
};
